import { Typography } from '@/components/UI/Typography'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** スポット名 */
  label: string
  /** このスポットで獲得したポイントの合計 */
  earnedPoint: number
  /** このスポットで獲得できるポイント */
  availablePoint: number
  /** 指定時はスポット名がタップ可能（下線付き button）になる。未指定時は下線なしの通常テキスト */
  onClick?: () => void
}

/**
 * スポットごとの獲得状況の 1 行（#3582）。
 * 表示項目は v1 の「スポットごとの獲得状況」を踏襲し、見た目は PointHistoryItem に揃える。
 *
 * 2 列 × 2 段のグリッドで、上段（スポット名・獲得した合計）は文字の下端（ベースライン）、
 * 下段（見出し・獲得できるポイント）は上端を左右で揃える。
 */
export const SpotPointStatusRow: FC<Props> = ({ label, earnedPoint, availablePoint, onClick }) => {
  const { t } = useTranslation()

  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-4 py-3">
        {/* 上段左: スポット名 */}
        <div className="min-w-0 self-baseline">
          {onClick ? (
            // ラベル文字列そのものがアクセシブルネームになるため aria-label は付けない
            <button type="button" onClick={onClick} className="block w-full max-w-full bg-transparent p-0 text-left">
              <Typography variant="body3" component="span" className="block truncate underline">
                {label}
              </Typography>
            </button>
          ) : (
            <Typography variant="body3" component="p" className="truncate">
              {label}
            </Typography>
          )}
        </div>
        {/* 上段右: 獲得した合計（4 桁以上はカンマ区切り。獲得ダイアログ #3080 と揃える） */}
        <Typography variant="h3" component="p" className="self-baseline whitespace-nowrap text-right">
          {earnedPoint.toLocaleString('ja-JP')}pt
        </Typography>
        {/* 下段左: 獲得できるポイントの見出し（v1 の表示項目） */}
        <Typography variant="caption1" component="p" c="gray.6" className="self-start">
          {t('home.point_where_you_get_stamp', 'このスポットで獲得できるポイント')}
        </Typography>
        {/* 下段右: 獲得できるポイント */}
        <Typography variant="caption1" component="p" c="gray.6" className="self-start whitespace-nowrap text-right">
          {availablePoint.toLocaleString('ja-JP')}pt
        </Typography>
      </div>
      <div className="h-px w-full bg-gray-200" />
    </div>
  )
}
